/**
 * The live connection to the hub: GET /api/stream as Server-Sent Events.
 * Reconnects with backoff and jitter, and treats a silent stream as dead
 * (the hub pings every SSE_PING_MS).
 */
import { RECONNECT_MAX_MS, RECONNECT_MIN_MS, SSE_DEAD_AFTER_MS, SSE_EVENT_TYPES } from '@orgus/protocol';
import type { AuthEndReason, AuthEndedEvent, SseEventMap, SseEventType } from '@orgus/protocol';

/**
 * connecting: first attempt. live: messages flowing. reconnecting: lost, retrying.
 * offline: several attempts failed and the hub does not answer at all (still retrying).
 * hub-error: the hub answers, but with a server error. signed-out: the hub answers 401.
 * mock: demo data, no hub.
 */
export type ConnectionStatus = 'connecting' | 'live' | 'reconnecting' | 'offline' | 'hub-error' | 'signed-out' | 'mock';

export const CONNECTION_LABEL: Record<ConnectionStatus, string> = {
  connecting: 'Connecting',
  live: 'Live',
  reconnecting: 'Reconnecting',
  offline: 'Hub unreachable',
  'hub-error': 'Hub error',
  'signed-out': 'Signed out',
  mock: 'Demo data',
};

/** Not live and not demo data: what is on screen may be out of date. */
export function isStale(c: ConnectionStatus): boolean {
  return c === 'reconnecting' || c === 'offline' || c === 'hub-error' || c === 'signed-out';
}

/** A short drop is normal (the stream reconnects within seconds): the page stays current this long. */
export const STALE_QUIET_MS = 20_000;

/**
 * True when the page is showing data the hub can no longer confirm, so it should look out of
 * date and offer no changes: the hub is unreachable or failing, or a drop has lasted past the
 * quiet period. Never for demo data, and never when signed out (the sign-in screen takes over).
 */
export function hubDown(c: ConnectionStatus, mode: string, lastMessageAt: number | null, now: number): boolean {
  if (mode === 'mock' || !isStale(c) || c === 'signed-out') return false;
  if (c === 'reconnecting') return lastMessageAt != null && now - lastMessageAt >= STALE_QUIET_MS;
  return true;
}

/** What a probe of the hub found after the stream dropped: null when it answers normally. */
export type ProbeResult = 'offline' | 'hub-error' | 'signed-out' | null;

export type StreamHandlers = {
  [K in Exclude<SseEventType, 'auth-ended'> as `on${Capitalize<K>}`]?: (data: SseEventMap[K]) => void;
} & {
  onStatus?: (s: ConnectionStatus) => void;
  /**
   * The hub ended the stream because the credential that opened it no longer holds as it did
   * (SD-2). On `role-changed` the stream has already reconnected once; on any other reason it
   * has stopped for good and the status is 'signed-out'.
   */
  onAuthEnded?: (reason: AuthEndReason) => void;
};

/** A second role-changed within this long of the last one waits out the usual backoff instead of reconnecting at once. */
export const ROLE_RECONNECT_GAP_MS = 10_000;

export interface StreamOptions {
  url?: string;
  /** After this many failed attempts in a row the status becomes 'offline'. */
  offlineAfter?: number;
  /**
   * Asked after each drop (an EventSource never says why it failed): whether the hub is
   * unreachable, answering with an error, or refusing this browser. Without it, every
   * failure counts as unreachable.
   */
  probe?: () => Promise<ProbeResult>;
}

/** Exponential backoff with full jitter, bounded to [RECONNECT_MIN_MS, RECONNECT_MAX_MS]. */
export function backoffMs(attempt: number, random: () => number = Math.random): number {
  const ceil = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** Math.max(0, attempt));
  return Math.round(RECONNECT_MIN_MS + random() * (ceil - RECONNECT_MIN_MS));
}

/** Open the stream. Returns a function that closes it for good. */
export function connectStream(handlers: StreamHandlers, opts: StreamOptions = {}): () => void {
  const url = opts.url ?? '/api/stream';
  const offlineAfter = opts.offlineAfter ?? 3;
  let es: EventSource | null = null;
  let attempt = 0;
  let closed = false;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let deadTimer: ReturnType<typeof setTimeout> | null = null;
  let status: ConnectionStatus | null = null;
  let roleReconnectAt = -Infinity;

  const setStatus = (s: ConnectionStatus) => {
    if (s === status) return;
    status = s;
    handlers.onStatus?.(s);
  };

  const armDeadTimer = () => {
    if (deadTimer) clearTimeout(deadTimer);
    deadTimer = setTimeout(() => {
      // Nothing heard, not even a ping: the connection is dead even if the browser has not noticed.
      drop();
    }, SSE_DEAD_AFTER_MS);
  };

  const drop = () => {
    if (closed) return;
    es?.close();
    es = null;
    if (deadTimer) clearTimeout(deadTimer);
    attempt++;
    const unreachable = attempt >= offlineAfter ? 'offline' : 'reconnecting';
    if (!opts.probe) setStatus(unreachable);
    else {
      // Keep what the last probe found until this one answers.
      if (status !== 'hub-error' && status !== 'signed-out') setStatus('reconnecting');
      const at = attempt;
      opts.probe().then(
        (r) => {
          if (closed || status === 'live' || at !== attempt) return;
          setStatus(r === null ? 'reconnecting' : r === 'offline' ? unreachable : r);
        },
        () => undefined,
      );
    }
    retryTimer = setTimeout(open, backoffMs(attempt - 1));
  };

  /**
   * The hub's last frame on a stream it ended (SD-2). Never a reconnect loop: a role change
   * reconnects once, at once, under the new role; any other reason stops for good, since the
   * hub would only answer 401 (the sign-in screen takes over).
   */
  const authEnded = (reason: AuthEndReason) => {
    es?.close();
    es = null;
    if (deadTimer) clearTimeout(deadTimer);
    if (reason === 'role-changed') {
      const now = Date.now();
      if (now - roleReconnectAt >= ROLE_RECONNECT_GAP_MS) {
        roleReconnectAt = now;
        setStatus('reconnecting');
        handlers.onAuthEnded?.(reason);
        open();
      } else {
        handlers.onAuthEnded?.(reason);
        drop();
      }
      return;
    }
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    setStatus('signed-out');
    handlers.onAuthEnded?.(reason);
  };

  const open = () => {
    if (closed) return;
    if (status === null) setStatus('connecting');
    const source = new EventSource(url, { withCredentials: true });
    es = source;
    armDeadTimer();
    source.onopen = () => {
      armDeadTimer();
    };
    source.onerror = () => {
      if (es === source) drop();
    };
    for (const type of SSE_EVENT_TYPES) {
      source.addEventListener(type, (ev) => {
        if (es !== source) return;
        armDeadTimer();
        let data: unknown;
        try {
          data = JSON.parse((ev as MessageEvent<string>).data);
        } catch {
          return;
        }
        if (type === 'auth-ended') return authEnded((data as AuthEndedEvent).reason);
        if (type === 'state') {
          attempt = 0;
          setStatus('live');
        }
        const key = `on${type[0]!.toUpperCase()}${type.slice(1)}` as keyof StreamHandlers;
        (handlers[key] as ((d: unknown) => void) | undefined)?.(data);
      });
    }
  };

  open();

  return () => {
    closed = true;
    es?.close();
    es = null;
    if (retryTimer) clearTimeout(retryTimer);
    if (deadTimer) clearTimeout(deadTimer);
  };
}
